import React from 'react';
import { Auth } from 'aws-amplify';
import { isAWSError, AWSError } from '../../utils/aws';
import LoadingPage from '../../components/LoadingPage';
import { captureError } from '../../utils/errors';
import config from '../../configs';

export interface AuthenticationData {
  username?: string;
  sub?: string;
  version?: string;
  phonePrefix?: string;
  phoneNumber?: string;
  name?: string;
  origin?: string;
  companyID?: string;
  fromID?: string;
  emailVerified?: boolean;
  email?: string;
  userName?: string;
}

interface AuthenticationState {
  readonly loading?: boolean;
  readonly authenticated?: boolean;
  readonly userData?: AuthenticationData;
}

interface AuthenticationActions {
  getAuthUser: () => AuthenticationData | null;
  authenticate: (params?: any) => Promise<void>;
  signOut: () => Promise<void>;
}

export interface IAuthenticationContext {
  state: AuthenticationState;
  actions: AuthenticationActions;
}

const initialState: AuthenticationState = {
  loading: true,
  authenticated: false,
  userData: undefined,
};

export const AuthenticationContext = React.createContext<IAuthenticationContext>({
  state: initialState,
  actions: {
    getAuthUser: () => null,
    authenticate: async (params: any = {}) => { },
    signOut: async () => { },
  },
});

export const AuthenticationContextConsumer = AuthenticationContext.Consumer;
export const AuthenticationContextProvider = AuthenticationContext.Provider;

interface IAuthenticationProviderProps {
  children: React.ReactNode;
}

interface IAuthenticationProviderState {
  readonly data: AuthenticationState;
}

export default class AuthenticationProvider extends
  React.Component<IAuthenticationProviderProps, IAuthenticationProviderState> {
  constructor(props: IAuthenticationProviderProps) {
    super(props);
    this.state = {
      data: initialState,
    };
  }

  async componentDidMount() {
    await this.authenticate();
  }

  setStateAsync = (state: IAuthenticationProviderState): Promise<void> => new Promise((res) => {
    this.setState(state, res);
  });

  getAuthUser = () => {
    const { data: { authenticated, userData } } = this.state;
    if (!authenticated) {
      return null;
    }
    if (userData) {
      return userData;
    }
    return null;
  };

  handleError = async (err: any) => {
    const { data } = this.state;
    if (isAWSError(err)) {
      const awsErr = err as AWSError;
      if (awsErr.code === 'NotAuthorizedException') {
        await this.setStateAsync({
          data: { ...data, loading: false },
        });
        return;
      }
    }
    captureError(err);
    await this.setStateAsync({
      data: { ...data, loading: false },
    });
  };

  authenticate = async (params: any = {}): Promise<void> => {
    const { data } = this.state;
    await this.setStateAsync({ data: { ...data, loading: true } });
    let user;
    try {
      user = await Auth.currentAuthenticatedUser(params);
    } catch (err) {
      if (err !== 'not authenticated') {
        captureError(err);
      }
    }
    let authenticated = false;
    const newData = { ...data };
    if (user) {
      authenticated = true;
      if (user.attributes) {
        newData.userData = {
          sub: user.attributes.sub,
          version: user.attributes['custom:version'],
          phonePrefix: user.attributes['custom:phone_prefix'],
          phoneNumber: user.attributes.phone_number,
          name: user.attributes.name,
          origin: user.attributes['custom:origin'],
          companyID: user.attributes['custom:company_id'],
          fromID: user.attributes['custom:from_id'],
          emailVerified: config.isEmailVerificationEnabled ? user.attributes.email_verified : true,
          email: user.attributes.email,
          userName: user.getSignInUserSession().getIdToken().decodePayload()["cognito:username"]
        };
      }
    }
    await this.setStateAsync({
      data: {
        ...newData,
        loading: false,
        authenticated,
      },
    });
  };

  signOut = async () => {
    try {
      await Auth.signOut();
    } catch (err) {
      captureError(err);
    }
    await this.setStateAsync({
      data: {
        ...initialState,
        loading: false,
      },
    });
  };

  renderContent() {
    const { data: { loading } } = this.state;
    const { children } = this.props;

    if (loading) {
      return <LoadingPage isLoading />;
    }

    return children;
  }

  render() {
    const { data } = this.state;

    const value: IAuthenticationContext = {
      actions: {
        authenticate: this.authenticate,
        getAuthUser: this.getAuthUser,
        signOut: this.signOut,
      },
      state: data,
    };

    return (
      <AuthenticationContextProvider
        value={value}
      >
        {this.renderContent()}
      </AuthenticationContextProvider>
    );
  }
}
